import React, { useEffect, useRef, useState } from 'react';
import ReactYoutube from 'react-youtube';
import { useContextSelector } from 'use-context-selector';
import { PageVideo } from '@scormhero/shared';

import styles from './VideoYoutube.module.scss';
import { UIContext } from '../../context/UI/UIContext';
import { handleSetLastTimeAt } from '../VideoHtml/VideoHtml';

type VideoYoutubeProps = PageVideo & {
  url: string;
  globalState: any;
};

function VideoYoutube({
  resourceId,
  url,
  settings = {},
  globalState,
}: VideoYoutubeProps) {
  const { onPageCompleted, getCurrentPage, getTracking, patchTracking } =
    globalState;
  const { disableSeek } = settings;
  const currentPage = getCurrentPage();
  const pageTracking = getTracking(currentPage.resourceId);
  const pageCompleted = pageTracking?.completed;
  const startBlinkNext = useContextSelector(UIContext, (c) => c.startBlinkNext);
  const [lastTimeAt, setLastTimeAt] = useState<{
    resourceId: string; // using resourceId here to make sure is lastTime from THAT resourceId
    time: number;
  }>({ resourceId: '', time: 0 });
  const [startAtTime, setStartAtTime] = useState<number>();
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const onTimeUpdate = useRef<NodeJS.Timeout>();

  useEffect(() => {
    function onMount() {
      setStartAtTime(getTracking(resourceId)?.time);
      setIsMounted(true);
    }

    function onUnmount() {
      onTimeUpdate.current && clearInterval(onTimeUpdate.current);

      // reset in case next page is also a video
      setStartAtTime(0);
      setLastTimeAt({ resourceId: '', time: 0 });
      setIsMounted(false);
    }

    onMount();

    return () => onUnmount();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (
      isMounted &&
      lastTimeAt.time > 0 &&
      lastTimeAt.resourceId === resourceId // this check is required to avoid save time from previous video (if has in previous page)
    ) {
      patchTracking(resourceId, { time: lastTimeAt.time });
    }
  }, [lastTimeAt.time, lastTimeAt.resourceId, resourceId, isMounted]); // eslint-disable-line

  function getYoutubeVideoId(url: string): string {
    try {
      var regExp =
        /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
      var match = url.match(regExp);
      return match && match[7].length === 11 ? match[7] : '';
    } catch (err) {
      return '';
    }
  }

  if (!isMounted) return null;

  return (
    <ReactYoutube
      containerClassName={styles.Youtube}
      videoId={getYoutubeVideoId(url)}
      opts={{
        height: '100%',
        width: '100%',
        playerVars: {
          // https://developers.google.com/youtube/player_parameters
          autoplay: 1,
          iv_load_policy: 3,
          playsinline: 1,
          rel: 0,
          showinfo: 0,
          controls: disableSeek && !pageCompleted ? 0 : 1,
          disablekb: disableSeek && !pageCompleted ? 1 : 0,
          start: startAtTime,
        },
      }}
      onEnd={() => {
        onPageCompleted(() => startBlinkNext());
        // reset tracking for the next visit return to the begining of the video
        patchTracking(resourceId, { time: 0 });
      }}
      onReady={(e) => {
        onTimeUpdate.current = setInterval(() => {
          const currentTime = e.target.getCurrentTime();
          setLastTimeAt((prevTime) => {
            if (!currentTime) return prevTime;

            const _lastTime = handleSetLastTimeAt(prevTime.time, currentTime);

            return { resourceId, time: _lastTime };
          });
        }, 1000);
      }}
    />
  );
}

export default VideoYoutube;
